import type { Custody, ExternalSigningDeviceType } from '@infinex/types';
import type { IconComponent } from '@infinex/ui-icons';
import {
  DeviceLaptopPhoneIcon,
  HotWalletIcon,
  InfinexIcon,
  LedgerIcon,
  MultiSigIcon,
  TrezorIcon,
  VaultFillIcon,
} from '@infinex/ui-icons';

export type CustodyIconType =
  | 'infinex'
  | 'imported'
  | 'vault'
  | 'external-signer'
  | 'multi-sig'
  | 'ledger'
  | 'trezor';

// TODO: Bob search the usage for this
/**
 * Maps a semantic custody type to its corresponding icon component.
 *
 * @param custody - The semantic custody type
 * @returns The appropriate icon component
 */
export const getCustodyIcon = (custody: CustodyIconType): IconComponent => {
  switch (custody) {
    case 'infinex':
      return InfinexIcon;
    case 'imported':
      return HotWalletIcon;
    case 'vault':
      return VaultFillIcon;
    case 'external-signer':
      return DeviceLaptopPhoneIcon;
    case 'multi-sig':
      return MultiSigIcon;
    case 'ledger':
      return LedgerIcon;
    case 'trezor':
      return TrezorIcon;
  }
};

/**
 * Gets the custody icon type for an external signing device.
 *
 * @param deviceType - The device type (ledger or trezor)
 * @returns The specific device icon type, or 'external-signer' as fallback
 */
export const getExternalSignerIconType = (
  deviceType?: ExternalSigningDeviceType | null
): CustodyIconType => {
  if (deviceType === 'ledger') return 'ledger';
  if (deviceType === 'trezor') return 'trezor';
  return 'external-signer';
};

/**
 * Maps custody and isImported to a semantic custody type.
 *
 * @param custody - The custody type ('wallet' or 'vault')
 * @param isImported - Whether the wallet is imported (only relevant for 'wallet')
 * @returns The semantic custody type
 */
export const getCustodyType = (
  custody: Custody,
  isImported: boolean
): CustodyIconType => {
  if (custody === 'vault') {
    return 'vault';
  }

  if (custody === 'external-signer') {
    return 'external-signer';
  }

  if (custody === 'multi-sig') {
    return 'multi-sig';
  }

  return isImported ? 'imported' : 'infinex';
};
